import {
  Dialog, DialogContent, DialogTitle, IconButton, Tooltip,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import { Asset, CertificationCategory } from '@x-guard/xgac-types/xgac';
import { useState } from 'react';
import {
  Button,
  FieldProps, ReferenceField, useRecordContext, useTranslate,
} from 'react-admin';

type AssetCertification = NonNullable<Asset['certifications']>[number];

export const DocumentaryProofPreview = (props: {
  documentaryProof: string;
  imageStyle?: React.CSSProperties;
  pdfStyle?: React.CSSProperties;
}) => {

  if (props.documentaryProof.match(/\.(jpg|jpeg|png)(\?.*)?$/i)) {

    return (
      <img
        src={props.documentaryProof}
        alt="Documentary proof"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          ...props.imageStyle,
        }}
      />
    );

  }

  return (
    <iframe
      src={props.documentaryProof}
      title="Documentary proof"
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        ...props.pdfStyle,
      }}
    />
  );

};

const DocumentaryProofPreviewButton = (props: { documentaryProof: string }) => {

  const [open, setOpen] = useState(false);
  const translate = useTranslate();

  return (
    <>
      <Button
        startIcon={<AttachFileIcon/>}
        onClick={() => setOpen(true)}
        variant='contained'
        label='resources.assets.certifications.text.view_document'
        size="small"
        sx={{
          whiteSpace: 'nowrap',
        }}
      />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle className="flex-in-between">
          <span>Documentary proof</span>
          <IconButton aria-label={translate('general.text.close')} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <DocumentaryProofPreview
            documentaryProof={props.documentaryProof}
            imageStyle={{
              flex: 1,
              minHeight: 0,
            }}
            pdfStyle={{
              flex: 1,
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );

};

const DocumentaryProofValue = (props: { documentaryProof?: string | null }) => {

  const translate = useTranslate();
  const categoryRecord = useRecordContext<CertificationCategory>();

  if (!categoryRecord) {

    return null;

  }

  let proofContent: React.ReactNode;

  if (categoryRecord.isDocumentaryProofRequired) {

    proofContent = props.documentaryProof ? (
      <DocumentaryProofPreviewButton documentaryProof={props.documentaryProof} />
    ) : (
      <span>{translate('resources.assets.certifications.text.not_provided')}</span>
    );

  } else {

    proofContent = (
      <Tooltip
        title={translate('resources.assets.certifications.text.not_required_description')}
        placement="bottom"
        arrow
      >
        <span>{translate('resources.assets.certifications.text.not_required')}</span>
      </Tooltip>
    );

  }

  return (
    <>
      {proofContent}
    </>
  );

};

export const DocumentaryProofField = (props: FieldProps) => {

  const translate = useTranslate();
  const certificationRecord = useRecordContext<AssetCertification>();
  if (!certificationRecord) {

    return null;

  }

  return (
    <ReferenceField
      {...props}
      source="certificationCategory._id"
      reference="certification-categories"
      label={translate('resources.assets.certifications.fields.documentary_proof')}
      link={false}
    >
      <DocumentaryProofValue documentaryProof={certificationRecord.documentaryProof} />
    </ReferenceField>
  );

};
